import React, {
  createElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { GitBranch, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  BRANCH_CARD_MIN_WIDTH,
  BRANCH_GRID_GAP,
  branchEndsWithConditionStep,
  computeConditionBranchLayout,
  formatBranchSummary,
  isConditionStep,
  normalizeConditionConfig,
} from '../../utils/workflowConditionUtils';
import { getBranchStepsFromConfig } from '../../utils/workflowConditionBranchSteps';
import { WORKFLOW_CONNECTOR_LINE } from '../../utils/workflowCanvasViewportUtils';
import {
  buildProductionConditionBranchPathEntries,
  buildVerticalConnectorSegment,
  PRODUCTION_CONDITION_BRANCH,
} from '../../utils/workflowConnectorPaths';
import {
  buildConditionBranchGraphHeightKey,
  createBranchGraphHeightReporter,
  normalizeBranchGraphHeight,
  shouldReportBranchGraphHeight,
} from '../../utils/workflowConditionBranchGraphHeight';

const PLUS_SIZE = 28;
const SEGMENT_HEIGHT = WORKFLOW_CONNECTOR_LINE.segmentHeight;
const FORK_HEIGHT = PRODUCTION_CONDITION_BRANCH.childTopY;
const END_DROP_HEIGHT = 24;

const DEFAULT_NONE_BRANCH = { name: 'None', label: 'When none of the conditions are met', steps: [] };

function BranchLaneConnector({
  laneCenterX,
  laneWidth = BRANCH_CARD_MIN_WIDTH,
  insertIndex,
  branchKey,
  stepId,
  readOnly,
  onAddStepToBranch,
  theme,
  parentPath,
}) {
  const stroke = WORKFLOW_CONNECTOR_LINE.color;
  const totalHeight = SEGMENT_HEIGHT * 2 + PLUS_SIZE;
  const plusCenterY = SEGMENT_HEIGHT + PLUS_SIZE / 2;
  const localLaneCenterX = laneWidth / 2;
  const testId = `add-step-branch-${branchKey}-${insertIndex}`;

  const handleClick = () => onAddStepToBranch?.(stepId, branchKey, insertIndex, parentPath);

  return (
    <div
      className="relative mx-auto shrink-0"
      style={{ width: laneWidth, height: totalHeight }}
      data-workflow-condition-lane-connector
      data-workflow-condition-branch-id={branchKey}
      data-workflow-condition-branch-insert-index={insertIndex}
    >
      <svg
        width={laneWidth}
        height={totalHeight}
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        aria-hidden="true"
        data-workflow-connector-svg
      >
        <path
          d={buildVerticalConnectorSegment({
            mergeX: localLaneCenterX,
            fromY: 0,
            toY: totalHeight,
            extendEnd: 2,
          })}
          fill="none"
          stroke={stroke}
          strokeWidth={WORKFLOW_CONNECTOR_LINE.width}
          strokeLinecap="round"
          strokeLinejoin="round"
          data-workflow-connector-path="branch-lane-segment"
        />
      </svg>
      {readOnly || !onAddStepToBranch ? (
        <div
          className={cn(
            'absolute z-[2] flex h-7 w-7 items-center justify-center rounded-full border bg-white',
            theme === 'dark' ? 'border-zinc-700' : 'border-slate-200',
          )}
          style={{
            left: localLaneCenterX,
            top: plusCenterY,
            transform: 'translate(-50%, -50%)',
          }}
          data-workflow-condition-plus
          data-workflow-condition-branch-id={branchKey}
          data-workflow-condition-branch-insert-index={insertIndex}
        >
          <Plus size={14} className="text-slate-300" />
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            'absolute z-[2] flex h-7 w-7 items-center justify-center rounded-full border bg-white shadow-sm transition-colors',
            theme === 'dark'
              ? 'border-zinc-600 text-zinc-400 hover:border-emerald-500 hover:text-emerald-500'
              : 'border-slate-300 text-slate-500 hover:border-emerald-500 hover:text-emerald-600',
          )}
          style={{
            left: localLaneCenterX,
            top: plusCenterY,
            transform: 'translate(-50%, -50%)',
          }}
          data-testid={testId}
          data-workflow-condition-plus
          data-workflow-condition-branch-id={branchKey}
          data-workflow-condition-branch-insert-index={insertIndex}
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  );
}

function ConditionBranchLane({
  branch,
  index,
  noneBranch,
  branchSteps,
  laneWidth = BRANCH_CARD_MIN_WIDTH,
  laneCenterX,
  readOnly,
  onAddStepToBranch,
  stepId,
  theme,
  renderBranchStep,
  selectedBranchStepId,
  parentPath = [],
  renderNestedConditionBranch,
}) {
  const summary = branch.isNone
    ? noneBranch.label || 'When none of the conditions are met'
    : formatBranchSummary(branch);
  const title = branch.isNone ? (noneBranch.name || 'None') : (branch.name || `Branch ${index + 1}`);
  const branchKey = branch.id || (branch.isNone ? 'none' : `lane-${index}`);
  const resolvedLaneCenterX = laneCenterX ?? laneWidth / 2;
  const localLaneCenterX = laneWidth / 2;
  const branchCardOffset = Math.max(0, (laneWidth - BRANCH_CARD_MIN_WIDTH) / 2);
  const endsWithCondition = branchEndsWithConditionStep(branchSteps);
  const connectorSegmentCount = branchSteps.length === 0
    ? 1
    : (endsWithCondition ? branchSteps.length : branchSteps.length + 1);

  const renderStepSlot = (branchStep, stepIndex) => {
    const branchContext = {
      conditionStepId: stepId,
      branchId: branchKey,
      stepIndex,
      laneCenterX: resolvedLaneCenterX,
      laneWidth,
      parentPath,
      isSelected: selectedBranchStepId === branchStep.id,
    };

    return (
      <div
        className="relative w-full overflow-visible"
        data-workflow-branch-step-slot
        data-workflow-branch-step-card="true"
        data-workflow-branch-step-id={branchStep.id}
        data-workflow-nested-condition-wrapper={isConditionStep(branchStep) ? 'true' : undefined}
      >
        {renderBranchStep?.(branchStep, branchContext)}
        {isConditionStep(branchStep) && renderNestedConditionBranch ? (
          <div
            className="relative overflow-visible"
            style={{ width: laneWidth, minWidth: laneWidth }}
            data-workflow-nested-condition-branch
            data-workflow-nested-condition-step-id={branchStep.id}
            data-workflow-nested-wrapper-overflow-mode="visible"
          >
            {renderNestedConditionBranch(branchStep, {
              ...branchContext,
              parentPath: [...parentPath, { conditionStepId: stepId, branchId: branchKey }],
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col items-center overflow-visible"
      style={{ width: laneWidth, minWidth: laneWidth }}
      data-workflow-condition-lane
      data-workflow-condition-branch-id={branchKey}
      data-workflow-condition-lane-center-x={resolvedLaneCenterX}
      data-workflow-condition-lane-width={laneWidth}
      data-workflow-condition-branch-step-count={branchSteps.length}
    >
      <div
        className={cn(
          'rounded-xl border bg-white shadow-sm shrink-0',
          branch.isNone ? 'border-slate-200' : 'border-purple-200 border-b-4 border-b-purple-500',
        )}
        style={{
          width: BRANCH_CARD_MIN_WIDTH,
          marginLeft: branchCardOffset,
          marginRight: branchCardOffset,
        }}
        data-testid={`condition-branch-card-${branch.id || index}`}
        data-workflow-condition-branch-card
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
          <GitBranch size={14} className="text-purple-600 shrink-0" />
          <span className="text-sm font-semibold text-purple-700">{title}</span>
        </div>
        <div className="px-3 py-2 text-xs text-slate-500 min-h-[40px]">
          {branch.isNone ? summary : `If ${summary}`}
        </div>
      </div>

      {Array.from({ length: connectorSegmentCount }).map((_, insertIndex) => (
        <React.Fragment key={`${branchKey}-segment-${insertIndex}`}>
          <BranchLaneConnector
            laneCenterX={localLaneCenterX}
            laneWidth={laneWidth}
            insertIndex={insertIndex}
            branchKey={branchKey}
            stepId={stepId}
            readOnly={readOnly}
            onAddStepToBranch={onAddStepToBranch}
            theme={theme}
            parentPath={parentPath}
          />
          {insertIndex < branchSteps.length ? renderStepSlot(branchSteps[insertIndex], insertIndex) : null}
        </React.Fragment>
      ))}

      {!endsWithCondition ? (
        <div
          className={cn(
            'mt-1 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide',
            theme === 'dark' ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-500',
          )}
          data-workflow-condition-end
          data-workflow-condition-branch-id={branchKey}
        >
          End
        </div>
      ) : null}
    </div>
  );
}

/**
 * GHL-style downstream branch graph: edges + branch cards centered under condition.
 * Branch graph width is derived from branch count, not main-lane card width.
 */
function WorkflowConditionBranchCanvas({
  step,
  readOnly = false,
  onAddStepToBranch,
  renderBranchStep = null,
  selectedBranchStepId = null,
  theme = 'dark',
  onGraphHeightChange = null,
  parentPath = [],
  nested = false,
  containerLaneWidth = BRANCH_CARD_MIN_WIDTH,
}) {
  const canvasRef = useRef(null);
  const lastReportedHeightRef = useRef(null);
  const nestedLayoutReportRef = useRef(null);

  const config = isConditionStep(step) ? normalizeConditionConfig(step.config) : null;
  const branchStepsSig = branchStepsSignature(config);
  const graphHeightKey = buildConditionBranchGraphHeightKey(step.id, parentPath);

  const { lanes, noneBranch } = useMemo(() => {
    if (!isConditionStep(step)) {
      return { lanes: [], noneBranch: DEFAULT_NONE_BRANCH };
    }
    const normalized = normalizeConditionConfig(step.config);
    const normalizedNone = normalized.noneBranch || DEFAULT_NONE_BRANCH;
    return {
      lanes: [
        ...(normalized.branches || []),
        { ...normalizedNone, isNone: true },
      ],
      noneBranch: normalizedNone,
    };
  }, [step]);

  const branchLayout = useMemo(
    () => computeConditionBranchLayout(step),
    [step],
  );

  const laneCount = branchLayout.laneCount || Math.max(lanes.length, 2);
  const laneWidths = branchLayout.laneWidths.length
    ? branchLayout.laneWidths
    : Array.from({ length: laneCount }, () => BRANCH_CARD_MIN_WIDTH);
  const graphContentWidth = branchLayout.graphWidth || (
    laneWidths.reduce((sum, width, index) => sum + width + (index > 0 ? BRANCH_GRID_GAP : 0), 0)
  );
  const nestedContainerWidth = nested ? containerLaneWidth : graphContentWidth;
  const nestedGraphOffset = nested
    ? nestedContainerWidth / 2 - graphContentWidth / 2
    : 0;
  const parentCenterXLocal = graphContentWidth / 2;
  const childCenterXs = branchLayout.laneCenterXs.length
    ? branchLayout.laneCenterXs
    : laneWidths.map((width, index) => {
      const priorWidth = laneWidths.slice(0, index).reduce(
        (sum, laneWidth, laneIndex) => sum + laneWidth + (laneIndex > 0 || index > 0 ? BRANCH_GRID_GAP : 0),
        0,
      );
      return priorWidth + width / 2;
    });
  const stroke = WORKFLOW_CONNECTOR_LINE.color;

  const forkPathEntries = useMemo(() => (
    buildProductionConditionBranchPathEntries({
      parentCenterX: parentCenterXLocal,
      childCenterXs,
    })
  ), [childCenterXs, parentCenterXLocal]);

  const reportHeight = useCallback(
    createBranchGraphHeightReporter(onGraphHeightChange, lastReportedHeightRef),
    [onGraphHeightChange],
  );

  const handleNestedLayoutChange = useCallback(() => {
    if (!canvasRef.current) return;
    reportHeight(canvasRef.current.offsetHeight);
  }, [reportHeight]);

  useLayoutEffect(() => {
    lastReportedHeightRef.current = null;
    nestedLayoutReportRef.current = null;
  }, [step.id, branchStepsSig]);

  useLayoutEffect(() => {
    if (nested || !onGraphHeightChange || !canvasRef.current) {
      return undefined;
    }

    const measure = () => {
      if (canvasRef.current) {
        reportHeight(canvasRef.current.offsetHeight);
      }
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const element = canvasRef.current;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [branchStepsSig, lanes.length, nested, onGraphHeightChange, reportHeight, step.id]);

  useLayoutEffect(() => {
    if (!nested || !onGraphHeightChange || !canvasRef.current) {
      return undefined;
    }

    const measure = () => {
      if (!canvasRef.current) return;
      const nextHeight = normalizeBranchGraphHeight(canvasRef.current.offsetHeight);
      if (!shouldReportBranchGraphHeight(nestedLayoutReportRef.current, nextHeight)) {
        return;
      }
      nestedLayoutReportRef.current = nextHeight;
      onGraphHeightChange();
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const element = canvasRef.current;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [branchStepsSig, lanes.length, nested, onGraphHeightChange, step.id]);

  const renderNestedConditionBranch = useCallback((nestedStep, nestedContext) => (
    createElement(WorkflowConditionBranchCanvas, {
      step: nestedStep,
      readOnly,
      onAddStepToBranch,
      renderBranchStep,
      selectedBranchStepId,
      theme,
      onGraphHeightChange: handleNestedLayoutChange,
      parentPath: nestedContext.parentPath,
      nested: true,
      containerLaneWidth: nestedContext.laneWidth ?? BRANCH_CARD_MIN_WIDTH,
    })
  ), [handleNestedLayoutChange, onAddStepToBranch, readOnly, renderBranchStep, selectedBranchStepId, theme]);

  if (!isConditionStep(step) || !config) return null;

  return (
    <div
      className={cn(
        'relative max-w-none',
        nested ? 'overflow-visible' : 'overflow-x-auto',
      )}
      style={nested ? { width: nestedContainerWidth, minWidth: nestedContainerWidth } : undefined}
      data-workflow-branch-graph-scroll
      data-workflow-nested={nested ? 'true' : 'false'}
      data-workflow-nested-graph-overflow-mode={nested ? 'visible' : 'scroll'}
      data-workflow-nested-container-lane-width={nested ? nestedContainerWidth : undefined}
      data-testid={`condition-branch-scroll-${step.id}`}
    >
      <div
        ref={canvasRef}
        className="relative max-w-none py-2"
        style={{
          width: graphContentWidth,
          minWidth: graphContentWidth,
          marginLeft: nestedGraphOffset,
        }}
        data-testid={`condition-branch-canvas-${step.id}`}
        data-workflow-condition-branch-canvas
        data-workflow-branch-graph-layer
        data-workflow-condition-graph-width={graphContentWidth}
        data-workflow-condition-branch-width={graphContentWidth}
        data-workflow-nested-branch-graph-width={nested ? graphContentWidth : undefined}
        data-workflow-nested-graph-left={nested ? nestedGraphOffset : undefined}
        data-workflow-condition-parent-center-x={parentCenterXLocal}
        data-workflow-condition-graph-left={nested ? nestedGraphOffset : 0}
        data-workflow-condition-lane-count={laneCount}
        data-workflow-condition-lane-widths={laneWidths.join(',')}
        data-workflow-condition-graph-height-key={graphHeightKey}
      >
        <div
          className="pointer-events-none absolute left-0 top-0 w-full"
          style={{ height: FORK_HEIGHT }}
          aria-hidden="true"
          data-workflow-condition-fork-layer
        >
          {forkPathEntries.length > 0 ? (
            <svg
              width={graphContentWidth}
              height={FORK_HEIGHT}
              className="pointer-events-none absolute left-0 top-0 overflow-visible"
              data-workflow-connector-svg
              data-workflow-condition-connector-svg="true"
              data-testid="workflow-condition-connector-svg"
            >
              {forkPathEntries.map((entry, index) => (
                <path
                  key={`${entry.pathType}-${index}`}
                  d={entry.d}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={WORKFLOW_CONNECTOR_LINE.width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  data-workflow-connector-path={entry.pathType}
                  data-workflow-condition-branch-index={entry.branchIndex}
                  data-testid={
                    entry.pathType === 'condition-branch-fork'
                      ? `workflow-condition-branch-fork-${entry.branchIndex}`
                      : undefined
                  }
                />
              ))}
            </svg>
          ) : null}
        </div>

        <div
          className="mt-0 grid gap-4 overflow-visible"
          style={{
            paddingTop: FORK_HEIGHT,
            width: graphContentWidth,
            minWidth: graphContentWidth,
            gridTemplateColumns: laneWidths.map((width) => `${width}px`).join(' '),
          }}
          data-workflow-condition-branch-grid
          data-workflow-condition-effective-lane-widths={laneWidths.join(',')}
        >
          {lanes.map((branch, index) => (
            <ConditionBranchLane
              key={branch.id || `lane-${index}`}
              branch={branch}
              index={index}
              noneBranch={noneBranch}
              branchSteps={getBranchStepsFromConfig(config, branch.isNone ? 'none' : branch.id)}
              laneWidth={laneWidths[index] ?? BRANCH_CARD_MIN_WIDTH}
              laneCenterX={childCenterXs[index]}
              readOnly={readOnly}
              onAddStepToBranch={onAddStepToBranch}
              stepId={step.id}
              theme={theme}
              renderBranchStep={renderBranchStep}
              selectedBranchStepId={selectedBranchStepId}
              parentPath={parentPath}
              renderNestedConditionBranch={renderNestedConditionBranch}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function branchStepsSignature(config) {
  if (!config) return '';
  const branchParts = (config.branches || []).map((branch) => (
    `${branch.id}:${(branch.steps || []).map((item) => item.id).join(',')}`
  ));
  const noneSteps = (config.noneBranch?.steps || []).map((item) => item.id).join(',');
  return `${branchParts.join('|')};none:${noneSteps}`;
}

export default WorkflowConditionBranchCanvas;
